/**
 * #tag suggestion configuration for the TipTap Mention extension.
 *
 * - Trigger: `#`
 * - API: GET /api/tags/search?q=<query>
 * - On selection: inserts an inline tag node with id, name, and color
 * - If no matching tag exists, shows a "Create tag: <name>" option
 *   that POSTs to /api/tags to create it first
 */

import { PluginKey } from '@tiptap/pm/state';
import type { SuggestionOptions } from '@tiptap/suggestion';
import { apiGet, apiPost } from '../../lib/api';
import { createSuggestionRenderer } from './suggestion-renderer';
import type { SuggestionItem } from './SuggestionList';

// API response shape from GET /api/tags/search
interface TagSearchResult {
  id: string;
  name: string;
  color: string;
}

export const TagPluginKey = new PluginKey('mentionTag');

export const tagSuggestion: Omit<SuggestionOptions<SuggestionItem>, 'editor'> = {
  char: '#',
  pluginKey: TagPluginKey,

  items: async ({ query }) => {
    try {
      const results = await apiGet<TagSearchResult[]>(
        `/tags/search?q=${encodeURIComponent(query || '')}`,
      );
      const items: SuggestionItem[] = results.map(tagToItem);

      // If the user typed something and there's no exact match, offer to create it
      if (query && query.length > 0) {
        const exactMatch = results.some(
          (t) => t.name.toLowerCase() === query.toLowerCase(),
        );
        if (!exactMatch) {
          items.push({
            id: `__create__${query}`,
            label: query,
            color: '#6366f1', // default accent color for new tags
            isCreate: true,
          });
        }
      }

      return items;
    } catch {
      // If API fails but user typed something, still offer to create
      if (query && query.length > 0) {
        return [
          {
            id: `__create__${query}`,
            label: query,
            color: '#6366f1',
            isCreate: true,
          },
        ];
      }
      return [];
    }
  },

  command: ({ editor, range, props: item }) => {
    if (item.isCreate) {
      // Create the tag first, then insert the mention node
      apiPost<TagSearchResult>('/tags', { name: item.label })
        .then((created) => {
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .insertContent({
              type: 'mention',
              attrs: {
                id: created.id,
                label: created.name,
                mentionSuggestionChar: '#',
                color: created.color,
              },
            })
            .run();
        })
        .catch((err) => {
          console.error('Failed to create tag:', err);
          // Fall back to inserting with a placeholder id
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .insertContent({
              type: 'mention',
              attrs: {
                id: null,
                label: item.label,
                mentionSuggestionChar: '#',
                color: item.color,
              },
            })
            .run();
        });
    } else {
      // Normal selection — insert mention node directly
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .insertContent({
          type: 'mention',
          attrs: {
            id: item.id,
            label: item.label,
            mentionSuggestionChar: '#',
            color: item.color,
          },
        })
        .run();
    }
  },

  render: createSuggestionRenderer({ variant: 'tag' }),
};

function tagToItem(tag: TagSearchResult): SuggestionItem {
  return {
    id: tag.id,
    label: tag.name,
    color: tag.color,
  };
}
